/**
 * Zod schemas for the runtime approval-request flow (Issue #1559 — Stream D).
 *
 * Validates input at every boundary that constructs an approval — gRPC
 * handler in 13-workflow, tRPC routers in 2-api-gateway. Each input DTO in
 * `dtos/approval-request.dto.ts` has a matching schema here so callers can
 * `safeParse` before sending and the server can re-validate on receive.
 *
 * @see ./../dtos/approval-request.dto
 */

import { z } from 'zod';
import { UserRole } from '../enums/roles';

/** Native enum schema reused across this module to keep role validation aligned. */
const userRoleEnum = z.nativeEnum(UserRole);

/**
 * Schema for `RequestApprovalInput` — the originating action that requires
 * a human gate. Payload is sealed-but-typed via `z.record(z.unknown())` —
 * the owning service replays it verbatim, so we don't constrain shape here.
 *
 * `triggerUserRoles` (PR #1566 review fix A) is verified server-side against
 * `workflow.triggerRoles` before the request is opened — the UI's pre-check
 * is not authoritative on its own.
 */
export const requestApprovalInputSchema = z.object({
  businessId: z.string().uuid(),
  triggerAction: z.string().min(1),
  triggerUserId: z.string().uuid(),
  triggerUserRoles: z.array(userRoleEnum).min(1),
  payload: z.record(z.string(), z.unknown()),
  caseId: z.string().uuid().optional(),
  comment: z.string().max(2000).optional(),
});

/**
 * Base schema for `ApprovalDecisionInput`. Reason is optional on approve;
 * the deny variant tightens it to required (see `denyApprovalInputSchema`).
 *
 * `approverRoles` (PR #1566 review fix B) is verified server-side against
 * the resolved approver pool (post-escalation) before the decision lands —
 * any authed user with an approval id is otherwise able to approve.
 */
export const approvalDecisionInputSchema = z.object({
  businessId: z.string().uuid(),
  approvalId: z.string().uuid(),
  approverId: z.string().uuid(),
  approverRoles: z.array(userRoleEnum).min(1),
  reason: z.string().max(2000).optional(),
});

/**
 * Tightened variant for the deny path — reason MUST be present. The 13-workflow
 * service uses this schema (not the base) on `denyRequest` so the requestor and
 * audit log always see *why* the request was rejected.
 */
export const denyApprovalInputSchema = approvalDecisionInputSchema.extend({
  reason: z.string().min(1).max(2000),
});

/**
 * Schema for `CanPerformActionInput` — used by the `<ApprovalGatedButton>`
 * pre-render gate check.
 */
export const canPerformActionInputSchema = z.object({
  businessId: z.string().uuid(),
  triggerAction: z.string().min(1),
  payload: z.record(z.string(), z.unknown()),
  userId: z.string().uuid(),
  userRoles: z.array(userRoleEnum).min(1),
});

/**
 * Schema for `ListPendingApprovalsInput` — paginated approver-dashboard query.
 */
export const listPendingApprovalsInputSchema = z.object({
  businessId: z.string().uuid(),
  userId: z.string().uuid(),
  userRoles: z.array(userRoleEnum).min(1),
  scope: z.enum(['waitingForMe', 'myRequests', 'all']),
  limit: z.number().int().min(1).max(200).optional(),
  cursor: z.string().optional(),
});

/** Mirrors `ApprovalDecisionStatus` — the lifecycle status the service writes onto rows. */
const approvalDecisionStatusSchema = z.enum(['pending', 'approved', 'denied', 'escalated', 'expired']);

/** Mirrors `LoanApprovalAstorVerdict` — Astor's pre-decision read on the row. */
const loanApprovalAstorVerdictSchema = z.enum(['approve', 'review', 'hold', 'unknown']);

/**
 * Schema for `ApprovalRecord` — the persisted approval shape returned by
 * `getApprovalStatus` / `listPendingApprovals` / event payloads.
 *
 * `astorConfidence` is bounded `[0,100]` to catch malformed writes at the DTO
 * boundary (the persisted column is a plain `Int?` so a buggy prescreener
 * could otherwise stamp a negative or >100 score). Field stays optional /
 * nullable because the prescreener hasn't run on most rows yet.
 */
export const approvalRecordSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  bicWorkflowCode: z.string().min(1),
  triggerAction: z.string().min(1),
  triggerUserId: z.string().uuid(),
  triggerUserName: z.string().optional(),
  approverPool: z.array(userRoleEnum),
  approverIds: z.array(z.string().uuid()).optional(),
  approverId: z.string().uuid().optional(),
  status: approvalDecisionStatusSchema,
  payload: z.record(z.string(), z.unknown()),
  reason: z.string().optional(),
  comment: z.string().optional(),
  caseId: z.string().optional(),
  slaTargetHours: z.number(),
  dueAt: z.string(),
  createdAt: z.string(),
  decidedAt: z.string().optional(),
  astorVerdict: loanApprovalAstorVerdictSchema.nullable().optional(),
  /** 0-100 integer confidence score. Absent / null until the prescreener stamps the row. */
  astorConfidence: z.number().int().min(0).max(100).nullable().optional(),
  astorReasoningSummary: z.string().nullable().optional(),
});
